import { cva, type VariantProps } from 'class-variance-authority'
import type * as React from 'react'
import { cn } from '../lib/utils'
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  OctagonAlertIcon,
} from './icon'

const alertVariants = cva(
  'text-on-surface relative inline-grid items-start gap-x-2 gap-y-0.5 rounded-2xl border p-4 text-sm min-[32rem]:has-data-[slot=alert-action]:grid-cols-[1fr_auto] [&:not(:has([data-slot=alert-description]))]:items-center',
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        default:
          'border-surface-2 bg-surface-2 [&>[data-slot=alert-icon]]:text-on-surface',
        error:
          'border-surface-error bg-surface-error [&>[data-slot=alert-icon]]:text-on-surface-error [&_[data-slot=alert-description]]:text-on-surface-error-secondary',
        info: 'border-surface-info bg-surface-info [&>[data-slot=alert-icon]]:text-on-surface-info [&_[data-slot=alert-description]]:text-on-surface-info-secondary',
        success:
          'border-surface-success bg-surface-success [&>[data-slot=alert-icon]]:text-on-surface-success [&_[data-slot=alert-description]]:text-on-surface-success-secondary',
        warning:
          'border-surface-warning bg-surface-warning [&>[data-slot=alert-icon]]:text-on-surface-warning [&_[data-slot=alert-description]]:text-on-surface-warning-secondary',
      },
    },
  },
)

type AlertType = NonNullable<VariantProps<typeof alertVariants>['variant']>

function AlertRoot({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      className={cn(alertVariants({ variant }), className)}
      data-slot="alert"
      role="alert"
      {...props}
    />
  )
}

function AlertIcon({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        'bg-surface-1 absolute top-0 right-0 flex size-8 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full p-1.5 [&>svg]:size-full [&>svg]:overflow-visible',
        className,
      )}
      data-slot="alert-icon"
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('font-medium', className)}
      data-slot="alert-title"
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'text-on-surface-secondary flex flex-col gap-2.5',
        className,
      )}
      data-slot="alert-description"
      {...props}
    />
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'col-start-1 mt-2 flex flex-wrap justify-end gap-2 min-[32rem]:col-start-2 min-[32rem]:row-start-1 min-[32rem]:row-end-3 min-[32rem]:mt-0 min-[32rem]:self-center',
        className,
      )}
      data-slot="alert-action"
      {...props}
    />
  )
}

type AlertProps = Omit<
  React.ComponentProps<typeof AlertRoot>,
  'children' | 'title' | 'variant'
> & {
  type?: AlertType
  title?: React.ReactNode
  children?: React.ReactNode
  icon?: React.ReactNode | null
  action?: React.ReactNode
}

const defaultIcons: Record<AlertType, React.ReactNode> = {
  default: <InfoIcon />,
  error: <CircleAlertIcon />,
  info: <InfoIcon />,
  success: <CircleCheckIcon />,
  warning: <OctagonAlertIcon />,
}

const titleClassNames: Record<AlertType, string | undefined> = {
  default: undefined,
  error: 'text-on-surface-error',
  info: 'text-on-surface-info',
  success: 'text-on-surface-success',
  warning: 'text-on-surface-warning',
}

function Alert({
  type = 'default',
  title,
  children,
  icon,
  action,
  ...props
}: AlertProps) {
  const resolvedIcon = icon === undefined ? defaultIcons[type] : icon

  return (
    <AlertRoot variant={type} {...props}>
      {resolvedIcon !== null && <AlertIcon>{resolvedIcon}</AlertIcon>}
      {title !== undefined && (
        <AlertTitle className={titleClassNames[type]}>{title}</AlertTitle>
      )}
      {children !== undefined && (
        <AlertDescription>{children}</AlertDescription>
      )}
      {action !== undefined && <AlertAction>{action}</AlertAction>}
    </AlertRoot>
  )
}

export {
  Alert,
  AlertRoot,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  AlertAction,
  type AlertProps,
  type AlertType,
}
